"use client";

import { useState } from "react";
import { Check, Link } from "lucide-react";
import { cn } from "@/lib/utils";

export function HeadingAnchorCopy({
  id,
  label,
  className,
}: {
  id: string;
  /** The heading's text, so each button is announced distinctly. */
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const section = label?.trim();

  // navigator.clipboard is only available in secure contexts (HTTPS/localhost);
  // over a LAN IP or in some in-app browsers it's undefined, so fall back to the
  // legacy execCommand path and never let the click throw.
  async function copyText(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fall through to legacy */
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  async function onClick() {
    const url = new URL(window.location.href);
    url.hash = id;

    const ok = await copyText(url.toString());
    history.replaceState(null, "", `#${id}`);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        aria-label={
          section
            ? `Copy link to section: ${section}`
            : "Copy link to this section"
        }
        title={copied ? "Copied" : "Copy link"}
        className={cn(
          "mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-80 transition hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100",
          className,
        )}
      >
        {copied ? (
          <Check className="size-4" aria-hidden="true" />
        ) : (
          <Link className="size-4" aria-hidden="true" />
        )}
      </button>
      {/* Announces the result politely; the button's label stays stable. */}
      <span className="sr-only" role="status">
        {copied ? "Link copied" : ""}
      </span>
    </>
  );
}
